/**
 * Media Library Hooks
 *
 * Custom React hooks for managing state and side effects in the Media Library component.
 */

export { useUploadQueue } from './useUploadQueue';
export { useLabels, useWorkflowStatuses } from './useZapworksMetadata';
export type { UploadItem } from '../../UI/Uploader/types';
export { useDeleteAssets } from './useDeleteAssets';
export type { UseDeleteAssetsParams } from './useDeleteAssets';
export { useAssetMetadata } from './useAssetMetadata';
export type { AssetMetadata } from './useAssetMetadata';
export { useAssetNameEdit } from './useAssetNameEdit';
export type { UseAssetNameEditResult } from './useAssetNameEdit';
export { useDragSelect } from './useDragSelect';
export type { UseDragSelectOptions, UseDragSelectResult } from './useDragSelect';
export { useAssetSelection } from './useAssetSelection';
export type { UseAssetSelectionOptions, UseAssetSelectionResult } from './useAssetSelection';
export { useMediaLibraryUrlState } from './useMediaLibraryUrlState';
export type { UrlNavState, UseMediaLibraryUrlStateOptions, UseMediaLibraryUrlStateResult } from './useMediaLibraryUrlState';

import { useState, useEffect, useCallback, useRef } from 'react';
import type { IAsset, ProviderRegistry } from '../providers';
import { ZapworksProvider } from '../providers/zapworks';
import { AssetSource, MediaType, SortOption, Filters } from '../types';

/**
 * Parameters for useMediaAssets hook
 */
export interface UseMediaAssetsParams {
  registry: ProviderRegistry;
  mediaType?: MediaType | MediaType[];
  workspaceId?: string;
  source: AssetSource;
  searchQuery?: string;
  sortBy?: SortOption;
  filters?: Filters;
  page?: number;
  limit?: number;
  /** Enable infinite scroll / page accumulation. Defaults to true. */
  infiniteScroll?: boolean;
  /** Changing this value triggers a re-fetch (e.g. after delete/upload) */
  refreshKey?: number;
}

/**
 * Hook for loading media assets from providers with infinite scroll support.
 *
 * `page` sets the starting page (defaults to 1). Call `loadMore()` to append
 * subsequent pages. Any change to search/filter params resets back to `page`.
 * `hasMore` is false once a fetch returns fewer items than `limit`.
 */
export function useMediaAssets(params: UseMediaAssetsParams) {
  const { page: externalPage = 1 } = params;

  const [assets, setAssets] = useState<IAsset[]>([]);
  const [currentPage, setCurrentPage] = useState(externalPage);
  const [resetId, setResetId] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState(0);

  // Always hold the latest params without re-triggering the load effect
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  });

  // Reset to starting page whenever query-affecting params change.
  // Skipped on initial mount — the load effect handles the first fetch on its own.
  const hasMounted = useRef(false);
  // Reset hasMounted on unmount so Strict Mode remounts behave like a fresh mount.
  useEffect(() => () => { hasMounted.current = false; }, []);
  const { registry, mediaType, source, searchQuery, sortBy, filters, refreshKey } = params;
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    setCurrentPage(externalPage);
    setAssets([]);
    setHasMore(true);
    setTotalCount(0);
    setResetId(id => id + 1);
  }, [registry, mediaType, source, searchQuery, sortBy, filters, refreshKey, externalPage]);

  // Load a page of assets; depends only on currentPage + resetId so param
  // changes always go through the reset effect above first.
  useEffect(() => {
    let cancelled = false;
    const pg = currentPage;
    const {
      registry: reg,
      mediaType: mt,
      workspaceId,
      source: src,
      searchQuery: sq,
      sortBy: sb,
      filters: flt,
      limit: lim = 20,
      infiniteScroll: infinite = true,
    } = paramsRef.current;

    const loadAssets = async () => {
      if (pg === externalPage) {
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }
      setError(undefined);

      try {
        // Map AssetSource to provider ID, source filter, and workspace scope
        const { providerId, sourceFilter, needsWorkspaceId } = mapSourceToProvider(source);

        // Get the provider for the mapped provider ID
        const providerInstance = reg.get(providerId);

        if (!providerInstance) {
          throw new Error(`Provider not found: ${src}`);
        }

        const requestedTypes = Array.isArray(mt) ? mt : (mt ? [mt] : undefined);
        let supportedRequestedTypes: MediaType[] | undefined = requestedTypes;

        // Check if the provider supports the requested media types.
        // If mediaType is undefined (mixed mode), proceed and let the provider handle it (Zapworks provider supports mixed types).
        if (requestedTypes?.length) {
          const supportedTypes = providerInstance.getSupportedTypes();
          const unsupportedTypes = requestedTypes.filter(type => !supportedTypes.includes(type));
          if (unsupportedTypes.length) {
            console.warn(`Provider ${src} does not support requested types: ${unsupportedTypes.join(', ')}`);
            supportedRequestedTypes = requestedTypes.filter(type => supportedTypes.includes(type));
          }

          if (!supportedRequestedTypes?.length) {
            if (!cancelled) setAssets([]);
            return;
          }
        }

        const searchParams = {
          query: sq,
          types: supportedRequestedTypes,
          sort: sb,
          page: pg,
          limit: lim,
          source: sourceFilter,
          workspaceId: needsWorkspaceId ? workspaceId : undefined,
          label: flt?.labels?.length ? flt.labels : undefined,
          wfstatus: flt?.status?.length ? flt.status : undefined,
          publiclinkExperience: flt?.templateType?.length ? flt.templateType : undefined,
        };

        // Search for assets using the provider
        let results: IAsset[];
        let hasNextPage = false;
        if (providerInstance instanceof ZapworksProvider) {
          const { assets: zapAssets, count, next } = await providerInstance.searchWithCount(searchParams);
          results = zapAssets;
          hasNextPage = Boolean(next);
          if (!cancelled) setTotalCount(count);
        } else {
          results = await providerInstance.search(searchParams);
          hasNextPage = results.length >= lim;
        }

        // Apply client-side filters TODO: Move filtering to provider level if supported
        const filtered = results.filter(asset => {
          const metadata = asset.getMetadata();

          // Filter by animated
          if (flt?.animated?.length) {
            const hasAnimations = metadata.animations && Array.isArray(metadata.animations) && metadata.animations.length > 0;

            const filterValue = flt.animated[0] === 'true';
            if (hasAnimations !== filterValue) {
              return false;
            }
          }

          // Filter by categories
          if (flt?.categories?.length) {
            const assetCategories = metadata?.categories as string[] || [];
            const hasMatchingCategory = flt.categories.some(cat => assetCategories.includes(cat));
            if (!hasMatchingCategory) {
              return false;
            }
          }

          return true;
        });

        if (!cancelled) {
          if (infinite && pg !== externalPage) {
            setAssets(prev => [...prev, ...filtered]);
          } else {
            setAssets(filtered);
          }
          setHasMore(infinite && hasNextPage);
        }
      } catch (err) {
        // setError(err instanceof Error ? err.message : 'Failed to load assets');
        // setAssets([]);
        if (!cancelled) throw err;
      } finally {
        if (!cancelled) {
          if (pg === externalPage) setLoading(false);
          else setLoadingMore(false);
        }
      }
    };

    loadAssets();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resetId]);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      setLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  }, [loading, loadingMore, hasMore]);

  // totalCount is the server-side total for ZapworksProvider; falls back to
  // the number of loaded assets for providers that don't expose a count.
  return { assets, totalCount: totalCount || assets.length, loading, loadingMore, hasMore, loadMore, error };
}

/**
 * Maps AssetSource values to provider ID, optional source filter,
 * and whether the source requires workspace scoping.
 */
function mapSourceToProvider(source: AssetSource): { providerId: string; sourceFilter?: string; needsWorkspaceId: boolean } {
  switch (source) {
    case 'user-library':
      return { providerId: 'zapworks', sourceFilter: '', needsWorkspaceId: false };
    case 'workspace-library':
      return { providerId: 'zapworks', sourceFilter: '', needsWorkspaceId: true };
    case 'zappar-library':
      return { providerId: 'zapworks', sourceFilter: 'zappar', needsWorkspaceId: false };
    case 'trash':
      return { providerId: 'zapworks', sourceFilter: 'bin', needsWorkspaceId: true };
    default:
      // External providers (PolyHaven, Freesound, etc.) don't use workspace scoping
      return { providerId: source, needsWorkspaceId: false };
  }
}
